import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Save, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import WorkflowDiagram from "@/components/WorkflowDiagram";

const FIELDS = [
  ["functional_objective", "Functional objective"],
  ["dimensional_requirements", "Dimensional requirements"],
  ["material_requirements", "Material requirements"],
  ["loading_conditions", "Loading conditions"],
  ["environmental_conditions", "Environmental conditions"],
  ["manufacturing_constraints", "Manufacturing constraints"],
  ["safety_considerations", "Safety considerations"],
];

export default function Specification() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [spec, setSpec] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}`).then(({ data }) => {
      setProject(data);
      setSpec(data.specification || {});
    }).catch(() => toast.error("Project not found"));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${id}/specification`, spec);
      setProject(data);
      setSpec(data.specification);
      toast.success(`Specification saved — revision ${data.revision}`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await save();
      const { data } = await api.post(`/projects/${id}/approve-specification`);
      toast.success("Specification approved");
      nav(`/projects/${data.id}/workspace`);
    } catch { toast.error("Approval failed"); }
    finally { setApproving(false); }
  };

  if (!spec) return <div className="text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;

  const updateList = (key, idx, val) => setSpec((s) => ({ ...s, [key]: (s[key] || []).map((v, i) => i === idx ? val : v) }));
  const addToList = (key) => setSpec((s) => ({ ...s, [key]: [...(s[key] || []), ""] }));
  const removeFromList = (key, idx) => setSpec((s) => ({ ...s, [key]: (s[key] || []).filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-10">
      <div>
        <div className="font-tech mb-2">Stage 02 — Structured Specification</div>
        <h1 className="text-3xl lg:text-4xl font-semibold text-white">{project?.intent?.project_name}</h1>
        <p className="text-slate-400 mt-3 text-sm">
          Review, edit, and approve the proposed specification. Approval unlocks the project workspace.
        </p>
      </div>

      <WorkflowDiagram activeId="specification" compact />

      <div className="panel-elev p-6 lg:p-10 space-y-6" data-testid="specification-editor">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="label-cad">{label}</label>
            <textarea
              data-testid={`spec-${k}`}
              rows={k === "functional_objective" ? 3 : 4}
              className="input-cad resize-none font-mono text-[13px]"
              value={spec[k] || ""}
              onChange={(e) => setSpec((s) => ({ ...s, [k]: e.target.value }))}
            />
          </div>
        ))}

        {["missing_information", "assumptions_requiring_confirmation"].map((k) => (
          <div key={k}>
            <label className="label-cad">{k.replace(/_/g, " ")}</label>
            <div className="space-y-2">
              {(spec[k] || []).map((v, i) => (
                <div key={i} className="flex gap-2">
                  <input className="input-cad" value={v} onChange={(e) => updateList(k, i, e.target.value)} data-testid={`spec-${k}-${i}`} />
                  <button type="button" onClick={() => removeFromList(k, i)} className="btn-ghost text-xs">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => addToList(k)} className="btn-ghost text-xs" data-testid={`spec-${k}-add`}>+ Add item</button>
            </div>
          </div>
        ))}

        <div className="dim-line" />
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div className="font-tech">Revision {project?.revision || 1}</div>
          <div className="flex gap-3">
            <button onClick={save} className="btn-ghost" disabled={saving} data-testid="spec-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
            </button>
            <button onClick={approve} className="btn-primary" disabled={approving} data-testid="spec-approve">
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve & open workspace <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
